<template>
  <div class="event-impact-dashboard">
    <header class="page-header">
      <div>
        <h1><i class="fas fa-bolt"></i> Event Impact Dashboard</h1>
        <p class="subtitle">
          Declare market / regulatory events and see which clients are exposed.
        </p>
      </div>
      <button class="btn btn-primary" @click="openNewEvent">
        <i class="fas fa-plus"></i> Declare Event
      </button>
    </header>

    <section class="stats">
      <div class="stat">
        <div class="stat-label">Active events</div>
        <div class="stat-value">{{ activeEventCount }}</div>
      </div>
      <div class="stat">
        <div class="stat-label">Critical / High</div>
        <div class="stat-value danger">{{ highSeverityCount }}</div>
      </div>
      <div class="stat">
        <div class="stat-label">Households impacted (selected event)</div>
        <div class="stat-value">{{ impactCount }}</div>
      </div>
      <div class="stat">
        <div class="stat-label">Total exposure (selected event)</div>
        <div class="stat-value">{{ formatMoney(totalExposure) }}</div>
      </div>
    </section>

    <section class="two-col">
      <div class="col">
        <div class="panel">
          <div class="panel-header">
            <h3>Events</h3>
            <div class="controls">
              <input
                v-model="search"
                class="form-input"
                type="search"
                placeholder="Search events..."
              />
              <select v-model="severityFilter" class="form-input">
                <option value="">All severities</option>
                <option value="CRITICAL">Critical</option>
                <option value="HIGH">High</option>
                <option value="MEDIUM">Medium</option>
                <option value="LOW">Low</option>
              </select>
            </div>
          </div>
          <table class="data-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Type</th>
                <th>Severity</th>
                <th>Date</th>
                <th>Households</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="e in filteredEvents"
                :key="e.id"
                :class="{ selected: selectedEvent && selectedEvent.id === e.id }"
                @click="selectEvent(e)"
              >
                <td>{{ e.title }}</td>
                <td>{{ e.event_type }}</td>
                <td>
                  <span :class="['severity-badge', 'sev-' + e.severity.toLowerCase()]">
                    {{ e.severity }}
                  </span>
                </td>
                <td>{{ formatDate(e.event_date) }}</td>
                <td>{{ e.impacted_household_count }}</td>
                <td>
                  <button
                    class="btn btn-sm"
                    :disabled="computingId === e.id"
                    @click.stop="computeImpact(e)"
                  >
                    <i class="fas fa-calculator"></i>
                    {{ computingId === e.id ? 'Computing…' : 'Run impact' }}
                  </button>
                </td>
              </tr>
              <tr v-if="filteredEvents.length === 0">
                <td colspan="6" class="empty">No events match the current filter.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="col">
        <div class="panel">
          <div class="panel-header">
            <h3>
              Impacted households
              <small v-if="selectedEvent">({{ selectedEvent.title }})</small>
            </h3>
          </div>

          <div v-if="!selectedEvent" class="placeholder">
            Select an event to see which households are exposed.
          </div>

          <table v-else class="data-table">
            <thead>
              <tr>
                <th>Household</th>
                <th>Exposure $</th>
                <th>Exposure %</th>
                <th>Est. impact</th>
                <th>Notification</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="imp in impacts" :key="imp.id">
                <td>{{ imp.household_name }}</td>
                <td>{{ formatMoney(imp.exposure_value) }}</td>
                <td>{{ Number(imp.exposure_pct).toFixed(1) }}%</td>
                <td :class="{ negative: Number(imp.estimated_dollar_impact) < 0 }">
                  {{ formatMoney(imp.estimated_dollar_impact) }}
                </td>
                <td>
                  <span v-if="imp.notification_sent" class="tag tag-sent">Sent</span>
                  <span v-else-if="imp.notification_draft && imp.notification_draft.subject" class="tag tag-draft">Draft</span>
                  <span v-else class="tag tag-none">None</span>
                </td>
              </tr>
              <tr v-if="impacts.length === 0">
                <td colspan="5" class="empty">
                  No impacts computed yet. Click "Run impact" on the left.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </section>

    <!-- Declare event modal -->
    <div v-if="showEventModal" class="modal-backdrop" @click.self="showEventModal = false">
      <div class="modal">
        <h2>Declare Event</h2>
        <form @submit.prevent="saveEvent">
          <div class="form-row">
            <label>Title</label>
            <input v-model="eventForm.title" required class="form-input" />
          </div>
          <div class="form-row">
            <label>Type</label>
            <select v-model="eventForm.event_type" class="form-input">
              <option value="MARKET">Market</option>
              <option value="REGULATORY">Regulatory / Legal</option>
              <option value="SECURITY">Security-specific</option>
              <option value="CORP_ACTION">Corporate action</option>
            </select>
          </div>
          <div class="form-row">
            <label>Severity</label>
            <select v-model="eventForm.severity" class="form-input">
              <option value="CRITICAL">Critical</option>
              <option value="HIGH">High</option>
              <option value="MEDIUM">Medium</option>
              <option value="LOW">Low</option>
            </select>
          </div>
          <div class="form-row">
            <label>Description</label>
            <textarea v-model="eventForm.description" rows="3" class="form-input" />
          </div>
          <div class="form-row">
            <label>Affected symbols (comma-separated)</label>
            <input v-model="eventForm.affected_symbols_raw" class="form-input" placeholder="NVDA, TSLA" />
          </div>
          <div class="form-row">
            <label>Affected asset classes</label>
            <input
              v-model="eventForm.affected_asset_classes_raw"
              class="form-input"
              placeholder="US_EQUITY, FIXED_INCOME"
            />
          </div>
          <div class="form-row">
            <label>Affected sectors</label>
            <input
              v-model="eventForm.affected_sectors_raw"
              class="form-input"
              placeholder="TECHNOLOGY, ENERGY"
            />
          </div>
          <div class="modal-actions">
            <button type="button" class="btn" @click="showEventModal = false">Cancel</button>
            <button type="submit" class="btn btn-primary" :disabled="savingEvent">
              {{ savingEvent ? 'Saving…' : 'Save event' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api'
import { debounce } from 'lodash-es'

export default {
  name: 'EventImpactDashboard',
  data() {
    return {
      events: [],
      selectedEvent: null,
      impacts: [],
      search: '',
      severityFilter: '',
      computingId: null,
      showEventModal: false,
      savingEvent: false,
      eventForm: this.emptyEventForm(),
      debouncedLoad: null,
    }
  },
  computed: {
    activeEventCount() {
      return this.events.filter((e) => e.is_active).length
    },
    highSeverityCount() {
      return this.events.filter((e) => ['CRITICAL', 'HIGH'].includes(e.severity)).length
    },
    filteredEvents() {
      const q = (this.search || '').trim().toLowerCase()
      return this.events.filter((e) => {
        if (this.severityFilter && e.severity !== this.severityFilter) return false
        if (!q) return true
        return (
          (e.title || '').toLowerCase().includes(q) ||
          (e.description || '').toLowerCase().includes(q)
        )
      })
    },
    impactCount() {
      return this.impacts.length
    },
    totalExposure() {
      return this.impacts.reduce((sum, i) => sum + Number(i.exposure_value || 0), 0)
    },
  },
  watch: {
    search() {
      this.debouncedLoad()
    },
  },
  mounted() {
    this.debouncedLoad = debounce(this.loadEvents, 300)
    this.loadEvents()
  },
  methods: {
    emptyEventForm() {
      return {
        title: '',
        event_type: 'MARKET',
        severity: 'MEDIUM',
        description: '',
        affected_symbols_raw: '',
        affected_asset_classes_raw: '',
        affected_sectors_raw: '',
      }
    },
    async loadEvents() {
      try {
        const res = await api.get('/wealth/events/')
        this.events = res.data.results || res.data || []
      } catch (err) {
        console.error('loadEvents failed', err)
      }
    },
    async selectEvent(event) {
      this.selectedEvent = event
      await this.loadImpacts(event.id)
    },
    async loadImpacts(eventId) {
      try {
        const res = await api.get(`/wealth/events/${eventId}/impacts/`)
        this.impacts = res.data || []
      } catch (err) {
        console.error('loadImpacts failed', err)
        this.impacts = []
      }
    },
    async computeImpact(event) {
      this.computingId = event.id
      try {
        await api.post(`/wealth/events/${event.id}/compute-impact/`, {
          min_exposure_pct: 0,
        })
        await this.loadEvents()
        const fresh = this.events.find((e) => e.id === event.id)
        if (fresh) await this.selectEvent(fresh)
      } catch (err) {
        console.error('computeImpact failed', err)
        alert('Impact computation failed — see console.')
      } finally {
        this.computingId = null
      }
    },
    openNewEvent() {
      this.eventForm = this.emptyEventForm()
      this.showEventModal = true
    },
    async saveEvent() {
      this.savingEvent = true
      try {
        const payload = {
          ...this.eventForm,
          affected_symbols: this.splitCsv(this.eventForm.affected_symbols_raw).map((s) => s.toUpperCase()),
          affected_asset_classes: this.splitCsv(this.eventForm.affected_asset_classes_raw).map((s) => s.toUpperCase()),
          affected_sectors: this.splitCsv(this.eventForm.affected_sectors_raw).map((s) => s.toUpperCase()),
        }
        delete payload.affected_symbols_raw
        delete payload.affected_asset_classes_raw
        delete payload.affected_sectors_raw
        await api.post('/wealth/events/', payload)
        this.showEventModal = false
        await this.loadEvents()
      } catch (err) {
        console.error('saveEvent failed', err)
        alert('Could not save event — see console.')
      } finally {
        this.savingEvent = false
      }
    },
    splitCsv(s) {
      return (s || '')
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean)
    },
    formatDate(d) {
      if (!d) return '—'
      return new Date(d).toLocaleString()
    },
    formatMoney(v) {
      const num = Number(v || 0)
      return num.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })
    },
  },
}
</script>

<style scoped>
.event-impact-dashboard {
  padding: var(--spacing-lg, 24px);
  max-width: 1400px;
  margin: 0 auto;
}
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 24px;
}
.subtitle {
  color: #666;
  margin: 4px 0 0 0;
}
.stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
  margin-bottom: 24px;
}
.stat {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 12px 16px;
}
.stat-label { font-size: 12px; color: #6b7280; text-transform: uppercase; letter-spacing: 0.04em; }
.stat-value { font-size: 22px; font-weight: 600; margin-top: 4px; }
.stat-value.danger { color: #b91c1c; }

.two-col {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}
@media (max-width: 1000px) {
  .two-col { grid-template-columns: 1fr; }
}

.panel {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
}
.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
}
.panel-header h3 { margin: 0; font-size: 16px; }
.controls { display: flex; gap: 8px; }

.data-table { width: 100%; border-collapse: collapse; }
.data-table th, .data-table td { padding: 10px 12px; text-align: left; border-bottom: 1px solid #f3f4f6; font-size: 14px; }
.data-table tr { cursor: pointer; }
.data-table tr.selected { background: #eff6ff; }
.data-table tr:hover:not(.selected) { background: #fafafa; }
.data-table td.negative { color: #b91c1c; }
.data-table td.empty { text-align: center; color: #9ca3af; padding: 24px; }

.severity-badge {
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
}
.sev-critical { background: #fee2e2; color: #991b1b; }
.sev-high { background: #ffedd5; color: #9a3412; }
.sev-medium { background: #fef3c7; color: #92400e; }
.sev-low { background: #dcfce7; color: #166534; }

.tag { padding: 2px 8px; border-radius: 10px; font-size: 11px; }
.tag-sent { background: #dcfce7; color: #166534; }
.tag-draft { background: #fef3c7; color: #92400e; }
.tag-none { background: #f3f4f6; color: #6b7280; }

.placeholder { padding: 24px; color: #9ca3af; text-align: center; }
.form-input { padding: 6px 10px; border: 1px solid #d1d5db; border-radius: 6px; width: 100%; }
.btn { padding: 6px 12px; border: 1px solid #d1d5db; border-radius: 6px; background: #fff; cursor: pointer; }
.btn-primary { background: #2563eb; color: #fff; border-color: #2563eb; }
.btn-sm { padding: 4px 8px; font-size: 12px; }

.modal-backdrop {
  position: fixed; inset: 0; background: rgba(0,0,0,0.4);
  display: flex; align-items: center; justify-content: center; z-index: 1000;
}
.modal { background: #fff; border-radius: 10px; padding: 24px; width: 560px; max-width: 90vw; }
.form-row { margin-bottom: 12px; display: flex; flex-direction: column; gap: 4px; }
.modal-actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 16px; }
</style>
